import React from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useFechaHoy } from '../hooks/useFechaHoy';
import { fromIsoDate } from '../utils/dateHelpers';
import { theme } from '../config/theme';
import { ROLE_LABELS } from '../constants/roles';
import MobileHeaderMenu from './common/MobileHeaderMenu';

/*
 * Las acciones opcionales (Cierre Diario, Productores, Usuarios) se muestran solo
 * si App pasa su handler: así decide qué ve cada rol. `rolesOperativos` (solo
 * admin) convierte el rol en un selector para actuar como otro rol sin salir.
 */
const Header = ({
    user,
    userRole,
    onAddClientClick,
    onAddEspecieClick,
    onProductoresClick,
    onUsuariosClick,
    onSignOut,
    onCierreDiarioClick,
    rolesOperativos = null,
    onRolOperativoChange,
}) => {
    const isMobile = useMediaQuery();

    const fechaHoy = useFechaHoy();
    const today = fromIsoDate(fechaHoy).toLocaleDateString('es-AR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const formattedDate = today.charAt(0).toUpperCase() + today.slice(1);

    const styles = {
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 2rem',
            backgroundColor: theme.colors.primary,
            color: theme.colors.textPrimary,
            borderBottom: `3px solid ${theme.colors.primary}`,
            boxShadow: theme.shadows.soft,
            flexWrap: 'wrap',
            gap: '1rem',
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
        },
        logo: {
            fontFamily: theme.typography.logoFamily,
            fontWeight: '600',
            fontSize: '1.75rem',
            color: theme.colors.textPrimary,
        },
        date: {
            color: theme.colors.textSecondaryOnDark,
            fontSize: '0.875rem',
            fontWeight: '500',
            paddingLeft: '1.5rem',
            borderLeft: `1px solid ${theme.colors.textSecondaryOnDark}`,
        },
        headerRight: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
        },
        userInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            padding: '0.5rem 1rem',
            borderRadius: theme.borderRadius,
        },
        label: {
            opacity: 0.8,
            fontSize: '0.875rem',
        },
        // --- INICIO DE LA MEJORA ---
        // Estilo base para todos los botones
        button: {
            fontFamily: theme.typography.fontFamily,
            border: 'none',
            borderRadius: theme.borderRadius,
            padding: '0.6rem 1.2rem',
            fontSize: '0.875rem',
            fontWeight: '700', // 1. Aumentamos el peso a 'Bold' para mayor nitidez
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap',

            // 2. Propiedades para forzar un renderizado de fuente más nítido
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            transform: 'translateZ(0)', // 3. Pequeño truco para mejorar renderizado en transiciones
        },
        // --- FIN DE LA MEJORA ---
        primaryButton: {
            backgroundColor: theme.colors.surface,
            color: theme.colors.primary,
        },
        secondaryButton: {
            backgroundColor: 'transparent',
            color: theme.colors.textPrimary,
            border: `1px solid ${theme.colors.textSecondaryOnDark}`,
        },
        specialActionButton: {
            backgroundColor: '#4A90E2',
            color: theme.colors.textPrimary,
            border: `1px solid #4A90E2`,
        },
        dangerButton: {
            backgroundColor: theme.colors.errorText,
            color: theme.colors.textPrimary,
        },
        mobileDate: {
            color: theme.colors.textSecondaryOnDark,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        mobileButton: {
            width: '100%',
            minHeight: '46px',
            fontSize: '0.95rem',
        },
    };

    // Acciones del encabezado. En escritorio van en fila; en el celular, dentro
    // del menú desplegable y a lo ancho, para tocarlas cómodamente.
    const actions = [
        onCierreDiarioClick && {
            key: 'cierre',
            label: 'Ejecutar Cierre Diario',
            onClick: onCierreDiarioClick,
            style: styles.specialActionButton,
            hoverColor: '#357ABD',
        },
        {
            key: 'clientes',
            label: 'Clientes',
            onClick: onAddClientClick,
            style: styles.primaryButton,
            hoverColor: '#f0f0f0',
        },
        onProductoresClick && {
            key: 'productores',
            label: 'Productores',
            onClick: onProductoresClick,
            style: styles.secondaryButton,
            hoverColor: 'rgba(255, 255, 255, 0.1)',
            title: 'Padrón de productores externos sincronizado desde el Sistema de Gestión Comercial',
        },
        onUsuariosClick && {
            key: 'usuarios',
            label: 'Usuarios',
            onClick: onUsuariosClick,
            style: styles.secondaryButton,
            hoverColor: 'rgba(255, 255, 255, 0.1)',
            title: 'Roles de cada persona en la planilla',
        },
        {
            key: 'especie',
            label: '+ Agregar Especie',
            onClick: onAddEspecieClick,
            style: styles.secondaryButton,
            hoverColor: 'rgba(255, 255, 255, 0.1)',
        },
        {
            key: 'salir',
            label: 'Cerrar Sesión',
            onClick: onSignOut,
            style: styles.dangerButton,
            hoverColor: '#C11A1A',
        },
    ].filter(Boolean);

    const renderAction = ({ key, label, onClick, style, hoverColor, title }, extraStyle = {}) => (
        <button
            key={key}
            onClick={onClick}
            style={{ ...styles.button, ...style, ...extraStyle }}
            onMouseOver={e => e.currentTarget.style.backgroundColor = hoverColor}
            onMouseOut={e => e.currentTarget.style.backgroundColor = style.backgroundColor}
            title={title}
        >
            {label}
        </button>
    );

    const rolInfo = (
        <div style={styles.userInfo} onClick={rolesOperativos ? e => e.stopPropagation() : undefined}>
            <span style={styles.label}>{rolesOperativos ? 'Actuar como:' : 'Rol:'}</span>
            {rolesOperativos ? (
                <select
                    value={userRole}
                    onChange={e => onRolOperativoChange(e.target.value)}
                    aria-label="Actuar como"
                    title="Como admin podés operar con otro rol sin cerrar sesión"
                    style={{
                        fontFamily: theme.typography.fontFamily,
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        background: 'transparent',
                        color: theme.colors.textPrimary,
                        border: `1px solid ${theme.colors.textSecondaryOnDark}`,
                        borderRadius: theme.borderRadius,
                        padding: '0.15rem 0.35rem',
                        minHeight: isMobile ? '40px' : '30px',
                    }}
                >
                    {rolesOperativos.map(rol => (
                        <option key={rol} value={rol} style={{ color: theme.colors.primary }}>{ROLE_LABELS[rol]}</option>
                    ))}
                </select>
            ) : (
                <span style={{ fontWeight: '600' }}>{ROLE_LABELS[userRole] || userRole}</span>
            )}
        </div>
    );

    const logo = (
        <div style={isMobile ? { ...styles.logo, fontSize: '1.4rem' } : styles.logo}>
            axon <span style={{ fontWeight: '300' }}>capital</span>
        </div>
    );

    if (isMobile) {
        return (
            <header style={{ ...styles.header, padding: '0.75rem 1rem', flexWrap: 'nowrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    {logo}
                    <span style={styles.mobileDate}>
                        {formattedDate}
                    </span>
                </div>
                <MobileHeaderMenu>
                    <div style={styles.userInfo}>
                        <span style={styles.label}>Usuario:</span>
                        <span style={{ fontWeight: '600' }}>{user.displayName}</span>
                    </div>
                    {rolInfo}
                    {actions.map(action => renderAction(action, styles.mobileButton))}
                </MobileHeaderMenu>
            </header>
        );
    }

    return (
        <header style={styles.header}>
            <div style={styles.headerLeft}>
                {logo}
                <div style={styles.date}>
                    {formattedDate}
                </div>
            </div>
            <div style={styles.headerRight}>
                <div style={styles.userInfo}>
                    <span style={styles.label}>Usuario:</span>
                    <span style={{ fontWeight: '600' }}>{user.displayName}</span>
                </div>
                {rolInfo}
                {actions.map(action => renderAction(action))}
            </div>
        </header>
    );
};

export default Header;